/*
 * Diesel cart drawer — opens a right-side flyout (bottom sheet on mobile) when an
 * item is added to the bag, and when the header cart icon is tapped. Mirrors
 * uk.diesel.com's minicart. Markup + styles live in sections/diesel-cart-drawer.liquid.
 *
 * The drawer contents are kept in sync with the live cart using Shopify's Section
 * Rendering API: cart/add.js & cart/change.js are called with sections:'diesel-cart-drawer'
 * and the returned HTML replaces the drawer's inner markup.
 */
(function () {
  var SECTION = 'diesel-cart-drawer';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cssEscape(v) {
    if (window.CSS && CSS.escape) return CSS.escape(v);
    return String(v).replace(/["\\]/g, '\\$&');
  }

  class DieselCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.dcd__panel');
      this.bound = this.bound || this.bindEvents();
      window.dieselCartDrawer = this;
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-dcd-close]') || event.target.closest('[data-dcd-overlay]')) {
          this.close();
          return;
        }
        var minus = event.target.closest('[data-dcd-minus]');
        if (minus) { this.step(minus.dataset.key, -1); return; }
        var plus = event.target.closest('[data-dcd-plus]');
        if (plus) { this.step(plus.dataset.key, 1); return; }
        var remove = event.target.closest('[data-dcd-remove]');
        if (remove) { this.setQty(remove.dataset.key, 0); return; }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
      return true;
    }

    open() {
      // Anchor the drawer directly under the header ("flyout below the cart") on desktop.
      try {
        var header = document.querySelector('.site-header') || document.querySelector('header');
        if (header) {
          var bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
          this.style.setProperty('--dcd-top', bottom + 'px');
        }
      } catch (e) { /* no-op */ }
      this.classList.add('is-open');
      document.documentElement.classList.add('dcd-lock');
      if (this.panel) {
        try { this.panel.focus({ preventScroll: true }); } catch (e) { this.panel.focus(); }
      }
    }

    close() {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('dcd-lock');
    }

    // Replace the drawer's inner markup from a section-HTML string.
    replaceFrom(html) {
      if (!html) return false;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('diesel-cart-drawer');
      if (!fresh) return false;
      this.innerHTML = fresh.innerHTML;
      this.panel = this.querySelector('.dcd__panel');
      var count = fresh.getAttribute('data-count');
      if (count != null) this.setAttribute('data-count', count);
      this.syncCount(count != null ? parseInt(count, 10) : undefined);
      return true;
    }

    // Fetch the section fresh (used when a cart/*.js response has no sections payload).
    refresh() {
      return fetch(root() + '?sections=' + SECTION, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((data) => { this.replaceFrom(data[SECTION]); })
        .catch(() => {});
    }

    refreshAndOpen() {
      return this.refresh().then(() => this.open());
    }

    // Current quantity for a line key, read from the DOM.
    currentQty(key) {
      var item = this.querySelector('.dcd__item[data-key="' + cssEscape(key) + '"] [data-dcd-qtynum]');
      return item ? parseInt(item.textContent, 10) || 0 : 0;
    }

    step(key, delta) {
      this.setQty(key, Math.max(0, this.currentQty(key) + delta));
    }

    setQty(key, quantity) {
      this.classList.add('is-busy');
      fetch(root() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION })
      })
        .then((r) => r.json())
        .then((cart) => {
          if (cart && cart.sections && cart.sections[SECTION]) this.replaceFrom(cart.sections[SECTION]);
          else this.refresh();
          if (cart && typeof cart.item_count === 'number') this.syncCount(cart.item_count);
          // Keep the standalone /cart page in sync if the drawer is used there.
          if (document.body.classList.contains('template-cart')) window.location.reload();
        })
        .catch(() => {})
        .finally(() => { this.classList.remove('is-busy'); });
    }

    // Update the header cart badge(s).
    syncCount(count) {
      if (typeof count !== 'number' || isNaN(count)) return;
      document.querySelectorAll('.haction__count, [data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }
  }

  if (!customElements.get('diesel-cart-drawer')) {
    customElements.define('diesel-cart-drawer', DieselCartDrawer);
  }

  // ---- Intercept every add-to-cart form (PDP + any product form) ----
  // Capture phase so this pre-empts Dawn's <product-form> submit handler.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target && event.target.closest && event.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      var drawer = window.dieselCartDrawer;
      if (!drawer) return; // let the native form submit if the drawer isn't ready

      event.preventDefault();
      event.stopImmediatePropagation();

      var button = form.querySelector('[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');

      var fd = new FormData(form);
      fd.append('sections', SECTION);

      fetch(root() + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: fd })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.status) return; // add error (e.g. sold out) — leave PDP as-is
          if (data && data.sections && data.sections[SECTION] && drawer.replaceFrom(data.sections[SECTION])) {
            drawer.open();
          } else {
            drawer.refreshAndOpen();
          }
        })
        .catch(function () {})
        .finally(function () { if (button) button.removeAttribute('aria-busy'); });
    },
    true
  );

  // ---- Header cart icon opens the drawer (keeps /cart href as no-JS fallback) ----
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('.haction--cart, [data-dcd-toggle]');
    if (!trigger) return;
    var drawer = window.dieselCartDrawer;
    if (!drawer) return;
    event.preventDefault();
    drawer.refreshAndOpen();
  });

  // ---- Public hook for the quick-add "+" panels (assets/diesel-quick-add.js) ----
  window.dieselCartDrawerOpen = function () {
    if (window.dieselCartDrawer) window.dieselCartDrawer.refreshAndOpen();
  };
})();
